import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Phone, Leaf, Zap, BatteryCharging, CircleDollarSign } from "lucide-react";
import { BUSINESS } from "@/data/content";
import useSEO from "@/hooks/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const SERVICE_TIERS = [
  { amount: "Up to $5,000", label: "Lower-income households" },
  { amount: "Up to $3,500", label: "Moderate-income households" },
  { amount: "Up to $1,500", label: "Middle-income households" },
];

const SERVICE_RULES = [
  "The panel or service upgrade must be part of installing a qualifying heat pump — standalone upgrades don't qualify.",
  "You must register and receive your eligibility code before any work starts. Install first, apply after, and you're disqualified.",
  "Your contractor needs to be registered with the Home Performance Contractor Network — we are, and we handle the paperwork.",
];

const EV_TIERS = [
  { amount: "Up to $350", label: "Single-family home, duplex or townhouse" },
  { amount: "$3,000 + $600/stall", label: "Condo & strata buildings (EV Ready)" },
  { amount: "Up to $2,000", label: "Per Level 2 charger at workplaces" },
];

const EV_RULES = [
  "CleanBC Go Electric covers up to 50% of eligible equipment and installation costs for home chargers.",
  "On BC Hydro? You may be able to stack an extra rebate on top of CleanBC Go Electric — we check for you.",
  "Strata buildings need council approval first — a written approval letter before any work can start. We handle everything after that.",
];

export default function Rebates() {
  useSEO({
    title: "Electrical Rebates in BC — EV Charger & Service Upgrade Rebates | ElectroMax Systems",
    description: "CleanBC rebates for electrical service upgrades (up to $5,000) and EV charger installation (up to $350 homes, $3,000+ stratas, $2,000 workplaces). ElectroMax handles the paperwork.",
    jsonld: [
      breadcrumbSchema([
        { name: "Home", href: "/" },
        { name: "Rebates", href: "/rebates" },
      ]),
    ],
  });

  return (
    <div data-testid="rebates-page">
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-20 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8 em-rise">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">Rebates</span>
          </div>
          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2 em-rise">
            <Leaf className="w-3.5 h-3.5" strokeWidth={2.5} /> — Rebates &amp; Incentives
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[88px] leading-[0.95] tracking-tighter font-black em-rise" style={{ animationDelay: "80ms" }}>
            Get paid to
            <br />
            <span className="text-white/40">upgrade your power.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed em-rise" style={{ animationDelay: "160ms" }}>
            British Columbia will pay real money toward your electrical upgrade — if the paperwork is done right. Two programs cover almost everything we install. We check what you qualify for during your free estimate and handle the claim with you.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 em-rise" style={{ animationDelay: "240ms" }}>
            <a
              href="#service-upgrade-rebate"
              data-testid="rebates-jump-service"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#E60000] text-white font-bold px-5 py-3 text-sm transition-colors"
            >
              <Zap className="w-4 h-4 text-[#E60000]" strokeWidth={2.5} /> 01 · Service Upgrade Rebate
            </a>
            <a
              href="#ev-charger-rebate"
              data-testid="rebates-jump-ev"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-[#E60000] text-white font-bold px-5 py-3 text-sm transition-colors"
            >
              <BatteryCharging className="w-4 h-4 text-[#E60000]" strokeWidth={2.5} /> 02 · EV Charger Rebate
            </a>
          </div>
        </div>
      </section>

      {/* Section 01 — Service Upgrade Rebate (light) */}
      <section
        id="service-upgrade-rebate"
        data-testid="service-rebate-section"
        className="scroll-mt-24 py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— Section 01</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                Service Upgrade
                <br />
                <span className="text-black/40">Rebate.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed">
                The CleanBC Energy Savings Program pays income-qualified households to bring their electrical service up to modern demand — the panel upgrade your heat pump, EV charger, and renovation plans have been waiting for.
              </p>
              <p className="mt-4 text-base text-black/70 leading-relaxed">
                Upgrading from an old 60-amp or 100-amp panel to 200 amps is what unlocks everything else on this page. This rebate is how a lot of our customers get it done for a fraction of the sticker price.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/services/panel-upgrades"
                  data-testid="rebate-cta-panel"
                  className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
                >
                  Panel Upgrade Service <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
                <Link
                  to="/guides/electrical-panel-upgrade-cost-bc-2026"
                  data-testid="rebate-cta-panel-guide"
                  className="inline-flex items-center gap-2 border border-black/20 hover:border-black text-black font-bold px-6 py-4 text-sm transition-colors"
                >
                  2026 Cost Guide <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
                {SERVICE_TIERS.map((t, i) => (
                  <div key={i} data-testid={`service-tier-${i}`} className="bg-white p-6 md:p-8">
                    <CircleDollarSign className="w-5 h-5 text-[#E60000] mb-4" strokeWidth={2} />
                    <div className="font-display text-2xl md:text-3xl font-black tracking-tight">{t.amount}</div>
                    <div className="text-xs text-black/60 mt-2 leading-relaxed">{t.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border border-[#E4E4E7] bg-white p-6 md:p-8">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">Know before you start</div>
                <ul className="space-y-3">
                  {SERVICE_RULES.map((r, i) => (
                    <li key={i} className="flex gap-3 text-sm md:text-base text-black/75 leading-relaxed">
                      <span className="text-[#E60000] mt-1 shrink-0">▪</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 02 — EV Charger Rebate (dark) */}
      <section
        id="ev-charger-rebate"
        data-testid="ev-rebate-section"
        className="scroll-mt-24 py-24 lg:py-32 bg-[#0A0A0A] border-t border-[#262626]"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-xs tracking-[0.24em] uppercase text-white/50 mb-4">— Section 02</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
                EV Charger
                <br />
                <span className="text-white/40">Rebate.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
                CleanBC Go Electric pays toward your Level 2 home charger and the licensed installation that comes with it. We&rsquo;re a registered EV installer — the rebate paperwork is part of every charger job we do.
              </p>
              <p className="mt-4 text-base text-white/70 leading-relaxed">
                Strata and workplace installs qualify for much bigger amounts than single-family homes. If you&rsquo;re on a strata council or manage a building, this section is worth two minutes of your time.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/services/ev-charger-installation"
                  data-testid="rebate-cta-ev"
                  className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
                >
                  EV Charger Installation <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
                <Link
                  to="/guides/bc-ev-charger-rebates-2026"
                  data-testid="rebate-cta-ev-guide"
                  className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
                >
                  2026 EV Rebates Guide <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
                {EV_TIERS.map((t, i) => (
                  <div key={i} data-testid={`ev-tier-${i}`} className="bg-[#141414] p-6 md:p-8">
                    <BatteryCharging className="w-5 h-5 text-[#E60000] mb-4" strokeWidth={2} />
                    <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-white">{t.amount}</div>
                    <div className="text-xs text-white/60 mt-2 leading-relaxed">{t.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 border border-[#262626] bg-[#141414] p-6 md:p-8">
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">Know before you start</div>
                <ul className="space-y-3">
                  {EV_RULES.map((r, i) => (
                    <li key={i} className="flex gap-3 text-sm md:text-base text-white/75 leading-relaxed">
                      <span className="text-[#E60000] mt-1 shrink-0">▪</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative py-20 border-t border-[#262626] bg-[#0A0A0A] overflow-hidden">
        <div className="pointer-events-none absolute -bottom-40 -left-20 w-[480px] h-[480px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-3">— Not sure what you qualify for?</div>
            <p className="font-display text-2xl md:text-4xl font-bold tracking-tighter leading-tight text-white">
              We check every rebate you&rsquo;re eligible for during your free estimate — and walk you through the claim before we leave.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-wrap gap-3">
            <Link
              to="/#contact"
              data-testid="rebates-quote-cta"
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              Get a Free Quote <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <a
              href={BUSINESS.phoneHref}
              data-testid="rebates-call-cta"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
